import { ceil, floor, round } from 'lodash-es'
import { add, divide, evaluate, multiply, subtract } from 'mathjs'

type RoundingMode = 'down' | 'up'

/**
 * 加法
 * @param a 第一个数
 * @param b 第二个数
 * @param needRound 是否需要取整
 * @param precision 保留小数位数
 * @param mode 取整模式，'up' 表示向上取整，'down' 表示向下取整
 * @returns 结果
 */
export function addNumbers(a: number, b: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function addNumbers(a: number[], b?: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function addNumbers(a: number | number[], b = 0, needRound = true, precision: number = 2, mode: RoundingMode = 'down'): number {
  if (Array.isArray(a)) {
    const aResult = a.reduce((acc, cur) => add(acc, cur), 0)
    const result = add(aResult, b)
    return needRound ? roundResult(result, precision, mode) : result
  }
  const result = add(a, b)
  return needRound ? roundResult(result, precision, mode) : result
}

/**
 * 减法
 * @param a 第一个数
 * @param b 第二个数
 * @param needRound 是否需要取整
 * @param precision 保留小数位数
 * @param mode 取整模式，'up' 表示向上取整，'down' 表示向下取整
 * @returns 结果
 */
export function subtractNumbers(a: number, b: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function subtractNumbers(a: number[], b?: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function subtractNumbers(a: number | number[], b = 0, needRound = true, precision: number = 2, mode: RoundingMode = 'down'): number {
  if (Array.isArray(a)) {
    const aResult = a.reduce((acc, cur) => subtract(acc, cur), 0)
    const result = subtract(aResult, b)
    return needRound ? roundResult(result, precision, mode) : result
  }
  const result = subtract(a, b)
  return needRound ? roundResult(result, precision, mode) : result
}

/**
 * 乘法
 * @param a 第一个数
 * @param b 第二个数
 * @param needRound 是否需要取整
 * @param precision 保留小数位数
 * @param mode 取整模式，'up' 表示向上取整，'down' 表示向下取整
 * @returns 结果
 */
export function multiplyNumbers(a: number, b: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function multiplyNumbers(a: number[], b?: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function multiplyNumbers(a: number | number[], b = 1, needRound = true, precision: number = 2, mode: RoundingMode = 'down'): number {
  if (Array.isArray(a)) {
    const aResult = a.reduce((acc, cur) => multiply(acc, cur), 1)
    const result = multiply(aResult, b)
    return needRound ? roundResult(result, precision, mode) : result
  }
  const result = multiply(a, b)
  return needRound ? roundResult(result, precision, mode) : result
}

/**
 * 除法
 * @param a 第一个数
 * @param b 第二个数
 * @param needRound 是否需要取整
 * @param precision 保留小数位数
 * @param mode 取整模式，'up' 表示向上取整，'down' 表示向下取整
 * @returns 结果
 */
export function divideNumbers(a: number, b: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function divideNumbers(a: number[], b?: number, needRound?: boolean, precision?: number, mode?: RoundingMode): number
export function divideNumbers(a: number | number[], b = 1, needRound = true, precision: number = 2, mode: RoundingMode = 'down'): number {
  if (b === 0) {
    throw new Error('除数不能为0')
  }
  b = (b * 1) || 1
  if (Array.isArray(a)) {
    const aResult = a.reduce((acc, cur) => divide(acc, cur), 1)
    const result = divide(aResult, b)
    return needRound ? roundResult(result, precision, mode) : result
  }
  const result = divide(a, b)
  return needRound ? roundResult(result, precision, mode) : result
}

/**
 * 计算等式
 * @param equation 等式字符串
 * @param needRound 是否需要取整
 * @param precision 保留小数位数
 * @param mode 取整模式，'up' 表示向上取整，'down' 表示向下取整
 * @returns 结果
 */
export function calculateEquation(equation: string, needRound = true, precision: number = 2, mode: RoundingMode = 'down'): number {
  try {
    const result = evaluate(equation)
    return needRound ? roundResult(result, precision, mode) : result
  }
  catch {
    throw new Error('无效的等式')
  }
}

/**
 * 使用精度（小数位数）来格式化数值，避免出现不必要的多余位数。
 * 例如消解 0.1 + 0.2 = 0.30000000000000004 的情况。
 *
 * @export
 * @param {number} val
 * @param {number} precision 小数位数
 * @returns {number} 格式化后的数值
 */
export function formatDecimal(val: number, precision = 2): number {
  const base = 10 ** precision
  return round((val) * base) / base
}

/**
 * 根据取整模式处理结果
 * @param value 计算结果
 * @param precision 保留小数位数
 * @param mode 取整模式，'up' 表示向上取整，'down' 表示向下取整
 * @returns 结果
 */
function roundResult(value: number, precision: number = 2, mode: RoundingMode = 'down'): number {
  const formattedValue = formatDecimal(value, precision)
  if (mode === 'down') {
    return floor(formattedValue, precision)
  }
  else {
    return ceil(formattedValue, precision)
  }
}

// 安全的执行 Number() 避免出现 NaN
export function safeNumber(val: unknown): number {
  return Number(val) || 0
}
